import React, { createContext, ReactNode, useEffect } from 'react';
import CryptoJS from 'crypto-js';

interface ComponentUIDContextType {
    startObserving: () => void;
}

const ComponentUIDContext = createContext<ComponentUIDContextType | undefined>(undefined);

interface ComponentUIDProviderProps {
    children: ReactNode;
}

function generateUniqueID(element: HTMLElement, contentAware = false): string {
    let identifier = '';
    let parent = element.parentNode as HTMLElement;

    while (parent && parent !== document.body) {
        if (parent.getAttribute('data-component-uid')) {
            identifier = parent.getAttribute('data-component-uid') + '>' + identifier;
        }
        parent = parent.parentNode as HTMLElement;
    }

    identifier += element.tagName;
    // Array.from(element.attributes).forEach((attr) => {
    //     identifier += `[${attr.name}="${attr.value}"]`;
    // });
    identifier += `[class="${element.attributes.getNamedItem('class')?.value}"]`;
    identifier += `[style="${element.attributes.getNamedItem('style')?.value}"]`;
    identifier += contentAware ? element.textContent : '';

    const hash = CryptoJS.SHA256(identifier).toString(CryptoJS.enc.Hex).slice(0, 16);
    return hash;
}

function checkSetUID(element: HTMLElement) {
    if (!element.hasAttribute('data-chain-uid')) {
        element.setAttribute('data-chain-uid', generateUniqueID(element, false));
    }
    if (!element.hasAttribute('data-chain-content-uid')) {
        element.setAttribute('data-chain-content-uid', generateUniqueID(element, true));
    }
}

export const ComponentUIDProvider: React.FC<ComponentUIDProviderProps> = ({ children }) => {
    const startObserving = () => {
        const callback: MutationCallback = (mutationsList, observer) => {
            for (const mutation of mutationsList) {
                if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
                    mutation.addedNodes.forEach((node) => {
                        // Ensure the node is an element node (ignores text nodes and others)
                        if (node.nodeType === Node.ELEMENT_NODE) {
                            const element = node as HTMLElement;
                            checkSetUID(element);
                            element
                                .querySelectorAll('*')
                                .forEach((child) => checkSetUID(child as HTMLElement));
                        }
                    });
                }
            }
        };

        const observer = new MutationObserver(callback);

        observer.observe(document.body, {
            childList: true,
            subtree: true,
            attributes: false,
        });

        return () => observer.disconnect();
    };

    useEffect(() => {
        return startObserving();
    }, []);

    return (
        <ComponentUIDContext.Provider value={{ startObserving }}>
            {children}
        </ComponentUIDContext.Provider>
    );
};
